import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { AgendaProject, AgendaProjectsService } from '../../services/agenda-projects.service';
import { ProjectHistoryService } from '../../services/project-history.service';
import { AuthService } from '../../../../shared/service/auth.service';
import { UserService, User } from '../../../../shared/service/user.service';
import { RingoverService } from '../../services/ringover.service';
import { TransactionService } from '../../services/transaction.service';
import { origines as origineOptions } from '../../models/enum';

@Component({
  selector: 'app-agenda-projects',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ConfirmDialogModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
  ],
  templateUrl: './agenda-projects.component.html',
  styleUrl: './agenda-projects.component.css',
  providers: [ConfirmationService]
})
export class AgendaProjectsComponent implements OnInit {
  agendaProjects: AgendaProject[] = [];
  loading = false;
  convertingId: number | null = null;
  deletingId: number | null = null;
  searchTerm = '';
  selectedLeadStatus: string | null = null;
  selectedUserId: number | null = null;
  totalRecords = 0;
  rows = 100;
  first = 0;
  showDetailDialog = false;
  selectedAgendaProject: AgendaProject | null = null;
  todayOnly = true;
  // Vue archivée : affiche uniquement les RDV archivés (par défaut : actifs).
  showArchived = false;
  archivingId: number | null = null;
  editingItemId: number | null = null;
  editStartAt = '';
  savingDateId: number | null = null;

  // Édition inline de l'objet du RDV (AgendaProject.description)
  editingCommentId: number | null = null;
  editComment = '';
  savingCommentId: number | null = null;

  // Commentaire (source : Transaction.commentaire, identique historique/transactions)
  commentDialogVisible = false;
  commentDialogTitle = '';
  commentDialogText = '';
  commentDialogProjectId: string | null = null;
  savingCommentaire = false;

  isAdmin = false;
  currentUserId: number | null = null;
  userOptions: Array<{ label: string; value: number }> = [];

  origineFilter = origineOptions;

  // Statut lead (cycle de vie commercial) — remplace la colonne « Projet CAC ».
  // Référentiel unique : celui du CRM (STATUT_LEAD_OPTIONS de la fiche contact),
  // le contact étant la source de vérité. Un changement ici est propagé au
  // contact rattaché, qui le redescend sur tous ses RDV.
  // Transitions automatiques : nouveau (arrivée agenda), devis_envoye / qs_envoye
  // (pipe), nrp (Ringover non décroché), converti (pipe « Fermé gagné »).
  // Choix manuels : a_recontacter, non_qualifie, perdu_*.
  leadStatusOptions = [
    { label: 'Nouveau', value: 'nouveau' },
    { label: 'En cours - Devis Envoyé', value: 'devis_envoye' },
    { label: 'En cours - QS Envoyé', value: 'qs_envoye' },
    { label: 'A recontacter', value: 'a_recontacter' },
    { label: 'NRP', value: 'nrp' },
    { label: 'Non qualifié - Refus', value: 'non_qualifie' },
    { label: 'Perdu - Trop Cher', value: 'perdu_trop_cher' },
    { label: 'Perdu - Refus Assureur', value: 'perdu_refus_assureur' },
    { label: 'Converti en transaction', value: 'converti' }
  ];
  savingLeadStatusId: number | null = null;

  // Combinaisons Phase du pipe → statuts lead possibles (référentiel
  // « Relation Phase Statut » : la phase prime sur le statut du lead).
  // RDV sans transaction liée (pas de phase) : tous les statuts restent proposés.
  // NRP absent de « Devis envoyé » / « Relance » : une fois « En cours - Devis
  // Envoyé », le statut ne redevient jamais NRP (le rappel manqué se lit dans
  // resultat_appel) ; la valeur courante d'un lead resté NRP reste listée.
  private readonly phaseLeadStatusMap: Record<string, string[]> = {
    'Nouveau LEAD / RDV':             ['nouveau', 'nrp'],
    'Echange':                        ['a_recontacter', 'nrp'],
    'Devis envoyé':                   ['devis_envoye'],
    'Relance & objections en cours':  ['devis_envoye'],
    'Questionnaire médical envoyé':   ['qs_envoye'],
    'Fermé gagné':                    ['converti'],
    'Fermé perdu':                    ['perdu_trop_cher', 'perdu_refus_assureur', 'non_qualifie']
  };

  /** Statuts lead proposés pour un RDV : limités aux combinaisons valides de sa
   *  phase (la valeur courante reste toujours listée pour ne pas afficher vide). */
  leadStatusOptionsFor(item: AgendaProject): Array<{ label: string; value: string }> {
    const allowed = item.phase ? this.phaseLeadStatusMap[item.phase] : null;
    if (!allowed) return this.leadStatusOptions;
    const current = this.normalizeLeadStatus(item.statut_lead);
    return this.leadStatusOptions.filter(
      option => allowed.includes(option.value) || option.value === current
    );
  }

  /** Couleur du tag « Phase » — mêmes couleurs que l'écran Transactions. */
  getPhaseSeverity(phase?: string | null): 'success' | 'danger' | 'info' | 'warning' | 'secondary' {
    switch (phase) {
      case 'Fermé gagné':                   return 'success';
      case 'Fermé perdu':                   return 'danger';
      case 'Devis envoyé':
      case 'Questionnaire médical envoyé':  return 'info';
      case 'Relance & objections en cours': return 'warning';
      default:                              return 'secondary';
    }
  }

  /** Résultat d'appel (Transaction.resultat_appel) : OK / NRP, null = « Nouveau ». */
  getResultatAppelLabel(resultat?: string | null): string {
    return (resultat || '').trim() || 'Nouveau';
  }

  resultatAppelOptions = ['OK', 'NRP'];
  savingResultatAppelId: number | null = null;

  /** Édition manuelle du résultat d'appel (écrit Transaction.resultat_appel de
   *  la transaction liée au RDV, partagé avec l'historique et les transactions). */
  onResultatAppelChange(item: AgendaProject, value: string | null): void {
    if (!item.project_id || this.savingResultatAppelId !== null) return;
    const previous = item.resultat_appel ?? null;
    if (value === previous) return;
    this.savingResultatAppelId = item.id;
    this.transactionService.updateResultatAppelByProject(item.project_id, value).subscribe({
      next: (res) => {
        this.savingResultatAppelId = null;
        item.resultat_appel = res.resultat_appel;
        this.messageService.add({
          severity: 'success', summary: "Résultat d'appel",
          detail: this.getResultatAppelLabel(res.resultat_appel), life: 2000,
        });
      },
      error: (err) => {
        this.savingResultatAppelId = null;
        item.resultat_appel = previous;
        this.messageService.add({
          severity: 'error', summary: 'Erreur',
          detail: "Changement du résultat d'appel impossible (aucune transaction liée ?)",
        });
        console.error(err);
      },
    });
  }

  getResultatAppelSeverity(resultat?: string | null): 'success' | 'danger' | 'secondary' {
    switch ((resultat || '').trim().toUpperCase()) {
      case 'OK':  return 'success';
      case 'NRP': return 'danger';
      default:    return 'secondary';
    }
  }

  // Sélection multiple
  selectedProjects: AgendaProject[] = [];
  // Sélection de toute la table (toutes pages, avec les filtres actifs)
  allTableSelected = false;

  // Actions groupées
  selectedAction: string | null = null;
  selectedBulkStatus: string | null = null;
  selectedAssignUserId: number | null = null;
  updatingStatus = false;
  assigning = false;
  deletingSelection = false;
  archivingSelection = false;
  exportingExcel = false;

  sendingSmsId: number | null = null;
  agentProfile: User | null = null;

  // ---- Dialog SMS pré-rédigé ----
  smsDialogVisible  = false;
  smsDialogItem     : AgendaProject | null = null;
  smsDialogPhone    = '';
  smsDialogContent  = '';
  smsDialogSending  = false;

  constructor(
    private agendaProjectsService: AgendaProjectsService,
    private projectHistoryService: ProjectHistoryService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private ringoverService: RingoverService,
    private transactionService: TransactionService
  ) {
    // Commentaire modifié depuis une autre table (transactions, historique).
    this.transactionService.commentaireChanged$.subscribe(({ project_id, commentaire }) => {
      this.agendaProjects
        .filter(p => p.project_id === project_id)
        .forEach(p => (p.commentaire = commentaire));
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.currentUserId = this.getCurrentUserIdFromToken();
    if (this.isAdmin) {
      // Sélectionne par défaut le vendeur connecté dans le filtre.
      this.selectedUserId = this.currentUserId;
      this.loadUsers();
    }
    this.loadAgendaProjects();
  }

  private getCurrentUserIdFromToken(): number | null {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return null;
    }
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }
      const decoded = JSON.parse(atob(payload));
      const userId = Number(decoded?.user_id);
      return Number.isFinite(userId) && userId > 0 ? userId : null;
    } catch (error) {
      console.warn('Impossible de décoder user_id depuis le token JWT:', error);
      return null;
    }
  }

  loadUsers(): void {
    this.userService.getUsers().subscribe({
      next: (users) => {
        this.userOptions = (users || [])
          .filter(user => user.is_active !== false)
          .map(user => ({
            label: user.username || user.email,
            value: user.id
          }));
      },
      error: (error) => {
        console.error('Erreur chargement conseillers:', error);
      }
    });
  }

  loadAgendaProjects(offset: number = this.first, limit: number = this.rows): void {
    this.loading = true;
    this.first = offset;
    this.rows = limit;

    this.agendaProjectsService.getAgendaProjects(limit, offset, {
      search: this.searchTerm.trim() || undefined,
      statut_lead: this.selectedLeadStatus || undefined,
      today_only: this.todayOnly || undefined,
      user_id: (this.isAdmin && this.selectedUserId != null) ? this.selectedUserId : undefined,
      archived: this.showArchived || undefined
    }).subscribe({
      next: (response) => {
        this.agendaProjects = response.items || [];
        this.totalRecords = response.total || 0;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur chargement projets agenda:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Chargement échoué',
          detail: error?.error?.detail || 'Impossible de récupérer les projets agenda.'
        });
        this.loading = false;
      }
    });
  }

  onPageChange(event: any): void {
    this.loadAgendaProjects(event.first || 0, event.rows || this.rows);
  }

  onSearchChange(): void {
    this.loadAgendaProjects(0, this.rows);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedLeadStatus = null;
    this.selectedUserId = null;
    this.todayOnly = false;
    this.showArchived = false;
    this.loadAgendaProjects(0, this.rows);
  }

  refresh(): void {
    this.loadAgendaProjects(this.first, this.rows);
  }

  loadAgendaProject(item: AgendaProject): void {
    if (this.convertingId !== null) {
      return;
    }

    this.convertingId = item.id;
    this.agendaProjectsService.createProjectFromAgenda(item.id).subscribe({
      next: (response) => {
        this.convertingId = null;

        if (response.mode === 'load_existing' && response.project_id) {
          // Projet déjà créé lors d'une comparaison précédente → recharger
          const updatedItem = response.agenda_project;
          if (updatedItem) {
            this.agendaProjects = this.agendaProjects.map(p =>
              p.id === updatedItem.id ? updatedItem : p
            );
          }
          this.openProject(response.project_id);
          return;
        }

        // Mode pré-remplissage : naviguer vers le formulaire sans project_id
        if (response.prefill) {
          localStorage.setItem('agendaPrefill', JSON.stringify(response.prefill));
        }
        this.router.navigate(['/cac/comparaison']);
      },
      error: (error) => {
        console.error('Erreur chargement projet agenda:', error);
        this.messageService.add({
          severity: "error",
          summary: "Chargement echoue",
          detail: error?.error?.detail || "Impossible de charger le projet depuis l'agenda."
        });
        this.convertingId = null;
      }
    });
  }

  confirmDelete(item: AgendaProject): void {
    this.confirmationService.confirm({
      message: `Supprimer le projet agenda de ${this.getClientName(item)} ?`,
      header: 'Supprimer le projet agenda',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteAgendaProject(item)
    });
  }

  viewAgendaProject(item: AgendaProject): void {
    this.selectedAgendaProject = item;
    this.showDetailDialog = true;
  }

  closeDetailDialog(): void {
    this.showDetailDialog = false;
    this.selectedAgendaProject = null;
  }

  deleteAgendaProject(item: AgendaProject): void {
    this.deletingId = item.id;
    this.agendaProjectsService.deleteAgendaProject(item.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Projet supprimé',
          detail: 'Le projet agenda a été supprimé.'
        });
        this.deletingId = null;
        this.refresh();
      },
      error: (error) => {
        console.error('Erreur suppression projet agenda:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Suppression échouée',
          detail: error?.error?.detail || 'Impossible de supprimer le projet agenda.'
        });
        this.deletingId = null;
      }
    });
  }

  getClientName(item: AgendaProject): string {
    const personName = [item.prenom, item.nom].filter(Boolean).join(' ').trim();
    return personName || item.title || 'Client agenda';
  }

  /** Nom affiché dans le tableau : civilité + NOM uniquement (ex: "MR Rezgui"). */
  getClientShortName(item: AgendaProject): string {
    const shortName = [item.civilite, item.nom].filter(Boolean).join(' ').trim();
    return shortName || item.nom || item.title || 'Client agenda';
  }

  getClientInitials(item: AgendaProject): string {
    const source = this.getClientName(item);
    return source
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('') || 'A';
  }

  getVendeurName(item: AgendaProject): string {
    return item.owner_name || '-';
  }

  /** Nom de la personne ayant pris le RDV (premier nom dans le titre Google Agenda). */
  getRdvTakerName(item: AgendaProject): string | null {
    const fromVendeur = (item.nom_vendeur || '').trim();
    if (fromVendeur) {
      return fromVendeur;
    }
    const title = (item.title || '').trim();
    if (!title) {
      return null;
    }
    const firstToken = title.split(/[\s,\-–|/]+/).filter(Boolean)[0];
    return firstToken || null;
  }

  /** Normalise le statut lead (ancienne valeur « en_cours » → « devis_envoye »). */
  normalizeLeadStatus(value: string | null | undefined): string {
    const normalized = (value || 'nouveau').trim().toLowerCase();
    return normalized === 'en_cours' ? 'devis_envoye' : normalized;
  }

  getLeadStatusLabel(value: string | null | undefined): string {
    const normalized = this.normalizeLeadStatus(value);
    const match = this.leadStatusOptions.find(option => option.value === normalized);
    return match?.label || value || 'Nouveau';
  }

  onLeadStatusChange(item: AgendaProject, newValue: string): void {
    if (!newValue || this.savingLeadStatusId !== null) {
      return;
    }
    this.savingLeadStatusId = item.id;
    this.agendaProjectsService.updateAgendaProject(item.id, { statut_lead: newValue } as any).subscribe({
      next: (updated) => {
        this.agendaProjects = this.agendaProjects.map(p =>
          p.id === updated.id ? updated : p
        );
        this.savingLeadStatusId = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Statut lead mis à jour',
          detail: `RDV passé en « ${this.getLeadStatusLabel(newValue)} ».`
        });
      },
      error: (error) => {
        console.error('Erreur mise à jour statut lead:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Mise à jour échouée',
          detail: error?.error?.detail || 'Impossible de modifier le statut lead.'
        });
        this.savingLeadStatusId = null;
        this.refresh();
      }
    });
  }

  // ---- Mapping origine (aligné sur Project History) ----
  getOrigineColor(origine: string | null | undefined): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    switch (this.normalizeOrigineValue(origine)) {
      case 'FOURNISSEUR_DE_LEAD': return 'success';
      case 'RDV_LBC': return 'info';
      case 'RDV_DIRECT': return 'info';
      case 'TELEPHONE': return 'warning';
      case 'MultiEquipement': return 'warning';
      case 'INTERNET': return 'success';
      case 'COURTIER': return 'info';
      case 'PARRAINAGE': return 'warning';
      case 'Doublon': return 'danger';
      default: return 'secondary';
    }
  }

  getOrigineLabel(origine: string | null | undefined): string {
    const normalizedOrigine = this.normalizeOrigineValue(origine);
    const option = this.origineFilter.find(item => item.value === normalizedOrigine);
    if (option) {
      return option.label;
    }
    return origine || 'Non spécifié';
  }

  private normalizeOrigineValue(origine: string | null | undefined): string {
    if (!origine) {
      return '';
    }
    const normalized = origine
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();

    const aliases: Record<string, string> = {
      COMPARADISE: 'FOURNISSEUR_DE_LEAD',
      'LEAD FOURNISSEUR': 'FOURNISSEUR_DE_LEAD',
      'FOURNISSEUR DE LEAD': 'FOURNISSEUR_DE_LEAD',
      'LEAD ASSUREUR': 'RDV_LBC',
      'APPEL ENTANT': 'TELEPHONE',
      TELEPROSPECTION: 'RDV_DIRECT',
      'SITE WEB': 'INTERNET',
      APPORTEUR: 'COURTIER',
      MULTIEQUIPEMENT: 'MultiEquipement',
      'RDV LBC': 'RDV_LBC',
      'RDV DIRECT': 'RDV_DIRECT',
      'RDV AUTRES': 'RDV_DIRECT',
      TELEPHONE: 'TELEPHONE',
      'APPEL ENTRANT': 'TELEPHONE',
      DIRECT: 'MultiEquipement',
      'PARTENAIRE AUTRES': 'MultiEquipement',
      INTERNET: 'INTERNET',
      'SITE WEB BROKINS': 'INTERNET',
      COURTIER: 'COURTIER',
      'PARTENAIRE BANQUE': 'COURTIER',
      'FORCE DE VENTE': 'PARRAINAGE',
      PARRAINAGE: 'PARRAINAGE',
      DOUBLON: 'Doublon'
    };

    return aliases[normalized] ?? origine.trim();
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) {
      return `Aujourd'hui ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    }

    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  }

  getAgendaProjectDetailEntries(item: AgendaProject | null): Array<{ label: string; value: any }> {
    if (!item) {
      return [];
    }

    return [
      { label: 'Civilité', value: item.civilite },
      { label: 'Vendeur', value: item.owner_name },
      { label: 'RDV pris par', value: item.nom_vendeur },
      { label: 'Nom', value: item.nom },
      { label: 'Téléphone', value: item.telephone },
      { label: 'Email', value: item.email },
      { label: 'Origine', value: item.origine },
      { label: 'Objet', value: item.description },
      { label: 'Commentaire', value: item.commentaire },
      { label: 'Titre agenda', value: item.title },
      { label: 'Début RDV', value: this.formatDate(item.start_at) },
      { label: 'Fin RDV', value: this.formatDate(item.end_at) },
      { label: 'ID Google event', value: item.google_event_id },
      { label: 'Statut lead', value: this.getLeadStatusLabel(item.statut_lead) },
      { label: 'Archivé le', value: item.archived_at ? this.formatDate(item.archived_at) : null },
      { label: 'Projet CAC', value: item.project_id },
      { label: 'Créé le', value: this.formatDate(item.created_at) },
      { label: 'Mis à jour le', value: this.formatDate(item.updated_at) },
      { label: 'Payload original', value: item.raw_payload }
    ];
  }

  toggleTodayFilter(): void {
    this.todayOnly = !this.todayOnly;
    this.loadAgendaProjects(0, this.rows);
  }

  /** Bascule entre la vue des RDV actifs et celle des RDV archivés. */
  toggleArchivedFilter(): void {
    this.showArchived = !this.showArchived;
    this.clearSelection();
    this.loadAgendaProjects(0, this.rows);
  }

  /** Archive ou désarchive un RDV (réversible, rien n'est supprimé). */
  toggleArchive(item: AgendaProject): void {
    if (this.archivingId !== null) {
      return;
    }
    const archive = !item.is_archived;
    this.archivingId = item.id;
    this.agendaProjectsService.updateAgendaProject(item.id, { archived: archive }).subscribe({
      next: () => {
        this.archivingId = null;
        this.removeFromSelection(item);
        this.messageService.add({
          severity: 'success',
          summary: archive ? 'RDV archivé' : 'RDV désarchivé',
          detail: archive
            ? `Le RDV de ${this.getClientName(item)} a été archivé. Retrouvez-le via le filtre « Archivés ».`
            : `Le RDV de ${this.getClientName(item)} est de nouveau actif.`
        });
        this.refresh();
      },
      error: (error) => {
        console.error('Erreur archivage RDV:', error);
        this.messageService.add({
          severity: 'error',
          summary: archive ? 'Archivage échoué' : 'Désarchivage échoué',
          detail: error?.error?.detail || 'Impossible de modifier l\'archivage du RDV.'
        });
        this.archivingId = null;
      }
    });
  }

  startEditDate(item: AgendaProject): void {
    this.editingItemId = item.id;
    if (item.start_at) {
      const date = new Date(item.start_at);
      if (!Number.isNaN(date.getTime())) {
        const pad = (n: number) => n.toString().padStart(2, '0');
        this.editStartAt = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
      } else {
        this.editStartAt = '';
      }
    } else {
      this.editStartAt = '';
    }
  }

  cancelEditDate(): void {
    this.editingItemId = null;
    this.editStartAt = '';
  }

  saveDate(item: AgendaProject): void {
    if (!this.editStartAt) {
      return;
    }
    this.savingDateId = item.id;
    const newDate = new Date(this.editStartAt).toISOString();
    this.agendaProjectsService.updateAgendaProject(item.id, { start_at: newDate } as any).subscribe({
      next: (updated) => {
        this.agendaProjects = this.agendaProjects.map(p =>
          p.id === updated.id ? updated : p
        );
        this.editingItemId = null;
        this.editStartAt = '';
        this.savingDateId = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Date mise à jour',
          detail: 'La date du RDV a été modifiée.'
        });
      },
      error: (error) => {
        console.error('Erreur mise à jour date:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Mise à jour échouée',
          detail: error?.error?.detail || 'Impossible de modifier la date du RDV.'
        });
        this.savingDateId = null;
      }
    });
  }

  /** Ouvre le commentaire de la transaction liée (même valeur qu'en historique). */
  openComment(item: AgendaProject): void {
    this.commentDialogTitle = [this.getClientShortName(item), item.project_id]
      .filter(Boolean).join(' · ');
    this.commentDialogText = (item.commentaire || '').trim();
    this.commentDialogProjectId = item.project_id || null;
    this.commentDialogVisible = true;
  }

  /** Enregistre le commentaire : la table transactions et l'historique suivent.
   *  Indisponible tant que le RDV n'a pas de projet CAC (aucune transaction). */
  saveCommentaire(): void {
    const projectId = this.commentDialogProjectId;
    if (!projectId || this.savingCommentaire) return;
    const value = this.commentDialogText.trim();
    this.savingCommentaire = true;
    this.transactionService.updateCommentaireByProject(projectId, value).subscribe({
      next: () => {
        this.savingCommentaire = false;
        this.commentDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Commentaire enregistré', life: 2000 });
      },
      error: (error) => {
        this.savingCommentaire = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Enregistrement échoué',
          detail: error?.error?.detail || 'Impossible de modifier le commentaire.'
        });
      }
    });
  }

  // ---- Édition inline de l'objet du RDV ----
  startEditComment(item: AgendaProject): void {
    this.editingCommentId = item.id;
    this.editComment = item.description || '';
  }

  cancelEditComment(): void {
    this.editingCommentId = null;
    this.editComment = '';
  }

  saveComment(item: AgendaProject): void {
    if (this.savingCommentId !== null) {
      return;
    }
    this.savingCommentId = item.id;
    const newComment = this.editComment.trim();
    this.agendaProjectsService.updateAgendaProject(item.id, { description: newComment } as any).subscribe({
      next: (updated) => {
        this.agendaProjects = this.agendaProjects.map(p =>
          p.id === updated.id ? updated : p
        );
        this.editingCommentId = null;
        this.editComment = '';
        this.savingCommentId = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Objet enregistré',
          detail: "L'objet du RDV a été mis à jour."
        });
      },
      error: (error) => {
        console.error('Erreur mise à jour objet:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Mise à jour échouée',
          detail: error?.error?.detail || "Impossible de modifier l'objet du RDV."
        });
        this.savingCommentId = null;
      }
    });
  }

  hasActiveFilters(): boolean {
    return !!this.searchTerm.trim() || !!this.selectedLeadStatus || this.todayOnly || this.showArchived || this.selectedUserId != null;
  }

  // ---- Sélection multiple ----
  canSelectProject(item?: AgendaProject | null): boolean {
    if (!item) {
      return false;
    }
    if (this.isAdmin) {
      return true;
    }
    return this.currentUserId !== null && Number(item.user_id) === this.currentUserId;
  }

  hasSelectableVisibleProjects(): boolean {
    return this.agendaProjects.some(item => this.canSelectProject(item));
  }

  isProjectSelected(item: AgendaProject): boolean {
    if (this.allTableSelected && this.canSelectProject(item)) {
      return true;
    }
    return this.selectedProjects.some(p => p.id === item.id);
  }

  toggleProjectSelection(item: AgendaProject, event: any): void {
    if (!this.canSelectProject(item)) {
      return;
    }
    this.allTableSelected = false;
    if (event.checked) {
      if (!this.isProjectSelected(item)) {
        this.selectedProjects.push(item);
      }
    } else {
      this.removeFromSelection(item);
    }
  }

  toggleAllSelection(event: any): void {
    if (!this.hasSelectableVisibleProjects()) {
      return;
    }
    this.allTableSelected = false;
    const selectableVisible = this.agendaProjects.filter(item => this.canSelectProject(item));
    if (event.checked) {
      selectableVisible.forEach(item => {
        if (!this.isProjectSelected(item)) {
          this.selectedProjects.push(item);
        }
      });
    } else {
      this.selectedProjects = this.selectedProjects.filter(selected =>
        !selectableVisible.some(visible => visible.id === selected.id)
      );
    }
  }

  areAllVisibleSelected(): boolean {
    if (this.allTableSelected) {
      return this.hasSelectableVisibleProjects();
    }
    const selectableVisible = this.agendaProjects.filter(item => this.canSelectProject(item));
    return selectableVisible.length > 0 && selectableVisible.every(item => this.isProjectSelected(item));
  }

  areSomeVisibleSelected(): boolean {
    if (this.allTableSelected) {
      return true;
    }
    return this.agendaProjects.some(item => this.canSelectProject(item) && this.isProjectSelected(item));
  }

  removeFromSelection(item: AgendaProject): void {
    this.allTableSelected = false;
    this.selectedProjects = this.selectedProjects.filter(p => p.id !== item.id);
  }

  clearSelection(): void {
    this.allTableSelected = false;
    this.selectedProjects = [];
  }

  /** "Sélectionner tout le tableau" proposé quand la page visible est cochée et qu'il reste d'autres pages. */
  canSelectEntireTable(): boolean {
    return !this.allTableSelected &&
      this.areAllVisibleSelected() &&
      this.totalRecords > this.agendaProjects.length;
  }

  selectEntireTable(): void {
    if (!this.canSelectEntireTable()) {
      return;
    }
    this.allTableSelected = true;
  }

  getSelectedProjectsCount(): number {
    return this.allTableSelected ? this.totalRecords : this.selectedProjects.length;
  }

  // ---- Actions groupées ----
  get actionMenuOptions(): { label: string; value: string }[] {
    const options: { label: string; value: string }[] = [
      { label: 'Changer le statut lead de la sélection', value: 'change_status' }
    ];
    if (this.isAdmin) {
      options.push({ label: 'Attribuer la sélection', value: 'assign' });
    }
    options.push({
      label: this.showArchived ? 'Désarchiver la sélection' : 'Archiver la sélection',
      value: 'archive_selection'
    });
    options.push({ label: 'Exporter en Excel', value: 'export_excel' });
    options.push({ label: 'Supprimer la sélection', value: 'delete_selection' });
    return options;
  }

  onActionMenuChange(): void {
    if (this.selectedAction !== 'change_status') {
      this.selectedBulkStatus = null;
    }
    if (this.selectedAction !== 'assign') {
      this.selectedAssignUserId = null;
    }
  }

  isExecutingSelectedAction(): boolean {
    return this.updatingStatus || this.assigning || this.deletingSelection || this.archivingSelection || this.exportingExcel;
  }

  canExecuteSelectedAction(): boolean {
    if (this.isExecutingSelectedAction() || this.loading) {
      return false;
    }
    // L'export peut s'exécuter sans sélection (exporte le résultat filtré).
    if (this.selectedAction === 'export_excel') {
      return true;
    }
    // Les autres actions groupées nécessitent une sélection explicite
    // (la sélection complète du tableau est réservée à l'export).
    if (this.allTableSelected) {
      return false;
    }
    if (this.selectedProjects.length === 0) {
      return false;
    }
    if (this.selectedAction === 'change_status') {
      return !!this.selectedBulkStatus;
    }
    if (this.selectedAction === 'assign') {
      return this.isAdmin && !!this.selectedAssignUserId;
    }
    if (this.selectedAction === 'delete_selection' || this.selectedAction === 'archive_selection') {
      return true;
    }
    return false;
  }

  executeSelectedAction(): void {
    if (!this.canExecuteSelectedAction()) {
      return;
    }
    if (this.selectedAction === 'change_status') {
      this.changeSelectedStatus();
    } else if (this.selectedAction === 'assign') {
      this.assignSelected();
    } else if (this.selectedAction === 'delete_selection') {
      this.deleteSelected();
    } else if (this.selectedAction === 'archive_selection') {
      this.archiveSelected();
    } else if (this.selectedAction === 'export_excel') {
      this.exportToExcel();
    }
  }

  private changeSelectedStatus(): void {
    const targetStatus = this.selectedBulkStatus;
    if (!targetStatus || this.selectedProjects.length === 0) {
      return;
    }
    const count = this.selectedProjects.length;
    const statusLabel = this.getLeadStatusLabel(targetStatus);
    this.confirmationService.confirm({
      message: `Mettre ${count} RDV sélectionné(s) au statut lead « ${statusLabel} » ?`,
      header: 'Confirmer le changement de statut',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Confirmer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.updatingStatus = true;
        const requests = this.selectedProjects.map(item =>
          this.agendaProjectsService.updateAgendaProject(item.id, { statut_lead: targetStatus } as any)
        );
        forkJoin(requests).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Statut mis à jour',
              detail: `${count} RDV mis à jour vers « ${statusLabel} ».`
            });
            this.updatingStatus = false;
            this.selectedAction = null;
            this.selectedBulkStatus = null;
            this.clearSelection();
            this.refresh();
          },
          error: (error) => {
            console.error('Erreur mise à jour statut groupé:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Mise à jour échouée',
              detail: error?.error?.detail || 'Impossible de mettre à jour le statut des RDV sélectionnés.'
            });
            this.updatingStatus = false;
            this.refresh();
          }
        });
      }
    });
  }

  private assignSelected(): void {
    const targetUserId = this.selectedAssignUserId;
    if (!targetUserId || this.selectedProjects.length === 0) {
      return;
    }
    const count = this.selectedProjects.length;
    const targetUser = this.userOptions.find(u => u.value === targetUserId);
    const targetLabel = targetUser?.label || 'le conseiller sélectionné';
    this.confirmationService.confirm({
      message: `Attribuer ${count} RDV sélectionné(s) à ${targetLabel} ?`,
      header: 'Confirmer l\'attribution',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Confirmer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.assigning = true;
        const requests = this.selectedProjects.map(item =>
          this.agendaProjectsService.updateAgendaProject(item.id, { user_id: targetUserId } as any)
        );
        forkJoin(requests).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'RDV attribués',
              detail: `${count} RDV attribué(s) à ${targetLabel}.`
            });
            this.assigning = false;
            this.selectedAction = null;
            this.selectedAssignUserId = null;
            this.clearSelection();
            this.refresh();
          },
          error: (error) => {
            console.error('Erreur attribution groupée:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Attribution échouée',
              detail: error?.error?.detail || 'Impossible d\'attribuer les RDV sélectionnés.'
            });
            this.assigning = false;
            this.refresh();
          }
        });
      }
    });
  }

  private archiveSelected(): void {
    if (this.selectedProjects.length === 0) {
      return;
    }
    const archive = !this.showArchived;
    const count = this.selectedProjects.length;
    this.confirmationService.confirm({
      message: archive
        ? `Archiver ${count} RDV sélectionné(s) ? Ils resteront consultables via le filtre « Archivés ».`
        : `Désarchiver ${count} RDV sélectionné(s) ? Ils redeviendront actifs.`,
      header: archive ? 'Confirmer l\'archivage' : 'Confirmer le désarchivage',
      icon: 'pi pi-inbox',
      acceptLabel: archive ? 'Archiver' : 'Désarchiver',
      rejectLabel: 'Annuler',
      accept: () => {
        this.archivingSelection = true;
        const requests = this.selectedProjects.map(item =>
          this.agendaProjectsService.updateAgendaProject(item.id, { archived: archive })
        );
        forkJoin(requests).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: archive ? 'RDV archivés' : 'RDV désarchivés',
              detail: archive
                ? `${count} RDV archivé(s).`
                : `${count} RDV désarchivé(s).`
            });
            this.archivingSelection = false;
            this.selectedAction = null;
            this.clearSelection();
            this.refresh();
          },
          error: (error) => {
            console.error('Erreur archivage groupé:', error);
            this.messageService.add({
              severity: 'error',
              summary: archive ? 'Archivage échoué' : 'Désarchivage échoué',
              detail: error?.error?.detail || 'Impossible de modifier l\'archivage des RDV sélectionnés.'
            });
            this.archivingSelection = false;
            this.refresh();
          }
        });
      }
    });
  }

  private deleteSelected(): void {
    if (this.selectedProjects.length === 0) {
      return;
    }
    const count = this.selectedProjects.length;
    this.confirmationService.confirm({
      message: `Supprimer définitivement ${count} RDV sélectionné(s) ? Cette action est irréversible.`,
      header: 'Confirmer la suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deletingSelection = true;
        const requests = this.selectedProjects.map(item =>
          this.agendaProjectsService.deleteAgendaProject(item.id)
        );
        forkJoin(requests).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'RDV supprimés',
              detail: `${count} RDV supprimé(s).`
            });
            this.deletingSelection = false;
            this.selectedAction = null;
            this.clearSelection();
            this.refresh();
          },
          error: (error) => {
            console.error('Erreur suppression groupée:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Suppression échouée',
              detail: error?.error?.detail || 'Impossible de supprimer les RDV sélectionnés.'
            });
            this.deletingSelection = false;
            this.refresh();
          }
        });
      }
    });
  }

  private exportToExcel(): void {
    if (this.exportingExcel) {
      return;
    }
    const selectedIds = this.selectedProjects.map(item => item.id);
    // Sélection complète du tableau : export de tout le résultat filtré côté serveur.
    const useSelection = selectedIds.length > 0 && !this.allTableSelected;
    const options = useSelection
      ? {}
      : {
          export_all_matching: true,
          search: this.searchTerm.trim() || undefined,
          statut_lead: this.selectedLeadStatus || undefined,
          today_only: this.todayOnly || undefined,
          user_id: (this.isAdmin && this.selectedUserId != null) ? this.selectedUserId : undefined,
          archived: this.showArchived || undefined
        };

    this.exportingExcel = true;
    this.agendaProjectsService.exportAgendaProjectsExcel(useSelection ? selectedIds : [], options).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Export',
            detail: 'Aucun fichier à télécharger.'
          });
          this.exportingExcel = false;
          return;
        }
        const disposition = response.headers.get('content-disposition');
        const match = disposition ? /filename="?([^"]+)"?/.exec(disposition) : null;
        const filename = match?.[1] || `projets_agenda_${new Date().toISOString().slice(0, 10)}.xlsx`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.messageService.add({
          severity: 'success',
          summary: 'Export terminé',
          detail: useSelection
            ? `${selectedIds.length} RDV sélectionné(s) exporté(s).`
            : 'Projets agenda filtrés exportés.'
        });
        this.exportingExcel = false;
        this.selectedAction = null;
      },
      error: (error) => {
        console.error('Erreur export Excel agenda:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Export échoué',
          detail: error?.error?.detail || 'Impossible d\'exporter les projets agenda en Excel.'
        });
        this.exportingExcel = false;
      }
    });
  }

  private openProject(projectId: string): void {
    this.projectHistoryService.getProjectById(projectId).subscribe({
      next: (projectDetails) => {
        localStorage.setItem('projectToLoad', JSON.stringify(projectDetails));
        this.router.navigate(['/cac/comparaison'], {
          queryParams: { loadProject: projectId }
        });
        this.convertingId = null;
      },
      error: (error) => {
        console.error('Erreur chargement projet CAC:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Projet créé',
          detail: 'Le projet CAC existe, mais son chargement dans le formulaire a échoué.'
        });
        this.convertingId = null;
      }
    });
  }

  /** Retourne le numéro E.164 (+33…) ou null si absent/invalide. */
  agendaPhoneE164(item: AgendaProject): string | null {
    const raw = (item.telephone || '').trim();
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    if (!digits || digits.length < 8) return null;
    if (digits.startsWith('00')) return `+${digits.slice(2)}`;
    if (digits.startsWith('33')) return `+${digits}`;
    if (digits.startsWith('0')) return `+33${digits.slice(1)}`;
    return `+${digits}`;
  }

  agendaClientName(item: AgendaProject): string {
    return [item.prenom, item.nom].filter(Boolean).join(' ') || '';
  }

  /** Retourne "Cher" ou "Chère" selon la civilité du client. */
  private buildCiviliteGreeting(civilite: string): string {
    const civ = (civilite || '').trim().toLowerCase().replace(/\./g, '');
    const feminine = ['mm', 'mme', 'madame', 'mlle', 'mademoiselle'].includes(civ);
    return feminine ? 'Chère' : 'Cher';
  }

  /** Normalise la civilité au format SMS : Mme / M. */
  private formatCiviliteForSms(civilite: string): string {
    const civ = (civilite || '').trim().toLowerCase().replace(/\./g, '');
    switch (civ) {
      case 'mm':
      case 'mme':
      case 'madame':
      case 'mlle':
      case 'mademoiselle':
        return 'Mme';
      case 'm':
      case 'mr':
      case 'monsieur':
        return 'M.';
      default:
        return (civilite || '').trim();
    }
  }

  /** Construit la signature dynamique de l'agent connecté. */
  private buildAgentSignature(agent: User): string {
    const lines: string[] = [];
    const name = (agent.username || '').trim();
    if (name) lines.push(name);
    if (agent.telephone_fixe) lines.push(agent.telephone_fixe.trim());
    if (agent.telephone) lines.push(agent.telephone.trim());
    if (agent.email) lines.push(agent.email.trim());
    lines.push('Cabinet BROKINS');
    lines.push('www.brokins.fr');
    return lines.join('\n');
  }

  getSmsBody(item: AgendaProject, agent: User): string {
    const civilite = item.civilite || '';
    const nom = (item.nom || '').trim();
    const greeting = this.buildCiviliteGreeting(civilite);
    const salutation = [greeting, this.formatCiviliteForSms(civilite), nom].filter(Boolean).join(' ');

    let heure = '';
    if (item.start_at) {
      const d = new Date(item.start_at);
      if (!Number.isNaN(d.getTime())) {
        const h = d.getHours().toString().padStart(2, '0');
        const m = d.getMinutes().toString().padStart(2, '0');
        heure = `${h}H${m}`;
      }
    }

    const now = new Date();
    const m2 = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    const monthM2 = m2.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

    const sig = this.buildAgentSignature(agent);

    return [
      `${salutation},`,
      '',
      `Nous avions RDV téléphonique ce jour à ${heure} pour des économies sur votre taux d'assurance de prêt.`,
      `Nous sommes le 1er courtier en Assurance Emprunteur. Nous pouvons atteindre jusqu'à 60% d'économies dès le mois de ${monthM2} grâce à des accords particuliers avec nos assureurs.`,
      `Vous n'avez rien faire, juste 4 minutes de votre temps suffisent.`,
      `Prestation sans frais.`,
      '',
      `Renseignez-vous sur le sérieux de notre Société de Courtage.`,
      '',
      `Bien à vous`,
      '',
      sig
    ].join('\n');
  }

  /** Ouvre le dialog SMS pré-rédigé pour relecture avant envoi. */
  openSms(item: AgendaProject): void {
    const phone = this.agendaPhoneE164(item);
    if (!phone) {
      this.messageService.add({ severity: 'warn', summary: 'Numéro manquant', detail: 'Aucun numéro de téléphone renseigné.' });
      return;
    }

    const openDialog = (agent: User) => {
      this.smsDialogItem    = item;
      this.smsDialogPhone   = phone;
      this.smsDialogContent = this.getSmsBody(item, agent);
      this.smsDialogVisible = true;
    };

    if (this.agentProfile) {
      openDialog(this.agentProfile);
    } else {
      this.userService.getMyProfile().subscribe({
        next: (p) => { this.agentProfile = p; openDialog(p); },
        error: () => openDialog({ id: 0, username: '', email: '', is_active: true, role: 'USER', createdAt: null })
      });
    }
  }

  /** Envoi effectif depuis le dialog (après relecture). */
  sendSmsFromDialog(): void {
    if (!this.smsDialogItem || !this.smsDialogPhone || !this.smsDialogContent.trim()) return;
    this.smsDialogSending = true;
    this.sendingSmsId     = this.smsDialogItem.id;
    this.ringoverService.sendSms({ to_number: this.smsDialogPhone, content: this.smsDialogContent }).subscribe({
      next: () => {
        this.smsDialogSending = false;
        this.smsDialogVisible = false;
        this.sendingSmsId     = null;
        this.messageService.add({ severity: 'success', summary: 'SMS envoyé', detail: `SMS envoyé au ${this.smsDialogPhone}.`, life: 5000 });
      },
      error: (err) => {
        this.smsDialogSending = false;
        this.sendingSmsId     = null;
        this.messageService.add({ severity: 'error', summary: 'Échec SMS', detail: err?.error?.detail || 'Erreur envoi SMS.' });
      }
    });
  }

  openCall(item: AgendaProject): void {
    const phone = this.agendaPhoneE164(item) || (item.telephone || '').trim();
    if (!phone) return;

    // Arme le suivi NRP côté serveur : si l'appel Ringover n'est pas décroché,
    // le statut lead du RDV passe automatiquement en « NRP ».
    const e164 = this.agendaPhoneE164(item);
    if (e164) {
      this.ringoverService.trackAgendaCall({ agenda_project_id: item.id, to_number: e164 }).subscribe({
        next: (res) => {
          if (res?.status === 'skipped') {
            this.messageService.add({
              severity: 'warn',
              summary: 'Suivi NRP désactivé',
              detail: res?.message || "La clé Ringover n'a pas la permission 'Calls Read'.",
              life: 8000,
            });
          } else {
            this.scheduleNrpRefresh(item.id);
          }
        },
        error: (err) => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Suivi NRP indisponible',
            detail: err?.error?.detail || "Le suivi automatique du statut n'a pas pu être armé.",
            life: 6000,
          });
        }
      });
    }

    window.location.href = `tel:${phone}`;
  }

  /** Rafraîchit le RDV pour refléter un éventuel passage auto en NRP.
   *  Le suivi serveur couvre ~30s + 10×25s (≈ 4,7 min) : on sonde à 120s (cas
   *  rapide) puis à 300s (cas où l'appel apparaît tard dans /calls). */
  private scheduleNrpRefresh(agendaProjectId: number): void {
    const refresh = () => {
      this.agendaProjectsService.getAgendaProject(agendaProjectId).subscribe({
        next: (fresh) => {
          const before = this.agendaProjects.find(p => p.id === agendaProjectId)?.statut_lead;
          const becameNrp = before !== 'nrp' && (fresh.statut_lead || '').toLowerCase() === 'nrp';
          this.agendaProjects = this.agendaProjects.map(p =>
            p.id === agendaProjectId ? fresh : p
          );
          if (this.selectedAgendaProject?.id === agendaProjectId) {
            this.selectedAgendaProject = fresh;
          }
          if (becameNrp) {
            this.messageService.add({
              severity: 'info',
              summary: 'Statut mis à jour',
              detail: 'Appel sans réponse → RDV passé en NRP.',
            });
          }
        },
        error: () => {},
      });
    };
    setTimeout(refresh, 120000);
    setTimeout(refresh, 300000);
  }

}
